/**
 * ファイルの形式・大きさ・件数の決まり（02 5.5・9.8 D-108、要件書 F-21・付録 No.30、仕様書 12 章）。
 * 画面（accept 属性・事前チェック）と API（署名付き POST の条件・完了の登録の確認）で同じ定義を使う。
 * 拡張子と MIME の組が合わないものは拒否する（FILE_003）。
 */

export interface FileTypeDefinition {
  mime_type: string;
  /** 小文字・ドット付き */
  extensions: readonly string[];
}

const PDF: FileTypeDefinition = { mime_type: 'application/pdf', extensions: ['.pdf'] };
const JPEG: FileTypeDefinition = { mime_type: 'image/jpeg', extensions: ['.jpg', '.jpeg'] };
const PNG: FileTypeDefinition = { mime_type: 'image/png', extensions: ['.png'] };
const GIF: FileTypeDefinition = { mime_type: 'image/gif', extensions: ['.gif'] };
const XLSX: FileTypeDefinition = {
  mime_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  extensions: ['.xlsx'],
};
const XLS: FileTypeDefinition = { mime_type: 'application/vnd.ms-excel', extensions: ['.xls'] };
const DOCX: FileTypeDefinition = {
  mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  extensions: ['.docx'],
};
const DOC: FileTypeDefinition = { mime_type: 'application/msword', extensions: ['.doc'] };
const PPTX: FileTypeDefinition = {
  mime_type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  extensions: ['.pptx'],
};
const CSV: FileTypeDefinition = { mime_type: 'text/csv', extensions: ['.csv'] };
const TEXT: FileTypeDefinition = { mime_type: 'text/plain', extensions: ['.txt'] };
const ZIP: FileTypeDefinition = { mime_type: 'application/zip', extensions: ['.zip'] };

/** 添付（顧客・案件・取引・要員・契約） */
export const ATTACHMENT_FILE_TYPES: readonly FileTypeDefinition[] = [
  PDF,
  JPEG,
  PNG,
  GIF,
  XLSX,
  XLS,
  DOCX,
  DOC,
  PPTX,
  CSV,
  TEXT,
  ZIP,
];
/** 名刺（企業担当者） */
export const BUSINESS_CARD_FILE_TYPES: readonly FileTypeDefinition[] = [JPEG, PNG, GIF];
/** 公開フォームの添付 */
export const PUBLIC_FORM_FILE_TYPES: readonly FileTypeDefinition[] = [PDF, JPEG, PNG, GIF, XLSX, DOCX, TEXT];
/** CSV 取込（⑦） */
export const IMPORT_FILE_TYPES: readonly FileTypeDefinition[] = [CSV];

/** 1 ファイルの上限（10MB） */
export const MAX_ATTACHMENT_SIZE_BYTES = 10_485_760;
/** 名刺 1 枚の上限（5MB） */
export const MAX_BUSINESS_CARD_SIZE_BYTES = 5_242_880;
/** 1 つの紐付先あたりの添付の上限（論理削除済みを除く） */
export const MAX_ATTACHMENTS_PER_ENTITY = 20;
/** ファイル名の上限 */
export const MAX_FILE_NAME_LENGTH = 255;
/** 公開フォームの 1 回の発行で扱うファイルの数（02 API-PUBF-02） */
export const MAX_PUBLIC_UPLOAD_FILES = 5;

/** ファイル名の拡張子（小文字・ドット付き。無ければ空文字） */
export function fileExtensionOf(file_name: string): string {
  const dot = file_name.lastIndexOf('.');
  return dot <= 0 || dot === file_name.length - 1 ? '' : file_name.slice(dot).toLowerCase();
}

/** 形式が許可リストにあり、拡張子と MIME の組が合うか */
export function isAllowedFileType(types: readonly FileTypeDefinition[], file_name: string, mime_type: string): boolean {
  const extension = fileExtensionOf(file_name);
  return types.some((type) => type.mime_type === mime_type && type.extensions.includes(extension));
}

/**
 * 拡張子から申告する MIME を決める（画面）。ブラウザの File.type は OS によって違う（Windows の .csv が
 * application/vnd.ms-excel になる等）ため、許可リストの拡張子から MIME を引く。無ければ null（形式の誤り）。
 */
export function mimeTypeForFileName(types: readonly FileTypeDefinition[], file_name: string): string | null {
  const extension = fileExtensionOf(file_name);
  return types.find((type) => type.extensions.includes(extension))?.mime_type ?? null;
}

/** input type=file の accept 属性 */
export function acceptAttributeOf(types: readonly FileTypeDefinition[]): string {
  return [...new Set(types.flatMap((type) => [...type.extensions, type.mime_type]))].join(',');
}

/**
 * 大きさの表示（B・KB・MB。1,024 で単位を上げ、小数 1 桁を四捨五入）。
 * 243 UT-664：1,048,575 バイトは「1024.0 KB」（前は切り捨てで 1023.9 KB）。単位は値の大きさで決め、丸めで上げない
 */
export function fileSizeText(size_bytes: number): string {
  if (size_bytes < 1024) {
    return `${size_bytes} B`;
  }
  if (size_bytes < 1024 * 1024) {
    return `${(size_bytes / 1024).toFixed(1)} KB`;
  }
  return `${(size_bytes / (1024 * 1024)).toFixed(1)} MB`;
}
